import React from "react"
import {FormProvider, useForm} from "react-hook-form"
import {Trans, useTranslation} from "react-i18next"
import {toast} from "react-toastify"
import {zodResolver} from "@hookform/resolvers/zod"
import {format} from "date-fns"
import {z} from "zod"

import Button, {ButtonForm} from "../../../components/Button"
import {DateField} from "../../../components/formElements/DateField"
import {DropdownField} from "../../../components/formElements/Dropdown/Dropdown"
import {VariantModal} from "../../../components/Modal"
import {useSalesAssistantCreateTaskMutation} from "../../../queries/salesAssistant"
import requestError from "../../../services/requestError"
import {ASalesAssistantCreateTaskTypes, ASalesAssistantDeal} from "../../../services/types.generated"
import {commonTransComponents, enumToOptions} from "../../../utils/i18n"
import {validateNativeEnum} from "../../../utils/validation"

const validationSchema = z.object({
  task_type: validateNativeEnum(ASalesAssistantCreateTaskTypes),
  task_date: z.date(),
})

export const PlanActivity: React.FC<{deal: ASalesAssistantDeal}> = ({deal}) => {
  const {t} = useTranslation()
  const taskTypeOptions = enumToOptions("SalesAssistantTaskTypes", true)

  const [isModalVisible, setIsModalVisible] = React.useState(false)

  const salesAssistantTaskCreateMutation = useSalesAssistantCreateTaskMutation()

  const handleSubmit = React.useCallback(
    async (values: z.infer<typeof validationSchema>) => {
      try {
        await salesAssistantTaskCreateMutation.mutateAsync({
          deal_id: deal.id,
          task_type: values.task_type,
          task_date: format(values.task_date, "yyyy-MM-dd"),
        })
        setIsModalVisible(false)
        toast.success(t("SalesAssistant_Deals_PlanActivity_SuccessToast"))
      } catch (e) {
        requestError(e)
      }
    },
    [deal, salesAssistantTaskCreateMutation, t]
  )

  const methods = useForm<z.infer<typeof validationSchema>>({
    mode: "onSubmit",
    resolver: zodResolver(validationSchema),
    defaultValues: {
      task_type: ASalesAssistantCreateTaskTypes.Email,
      task_date: new Date(),
    },
  })

  return (
    <>
      <Button fullWidth={false} onClick={() => setIsModalVisible(true)} color={"blue"} size={"sm"}>
        {t("SalesAssistant_Deals_PlanActivity_Button")}
      </Button>
      <VariantModal
        noIcon={true}
        size={"lg"}
        title={t("SalesAssistant_Deals_PlanActivity_Title")}
        isOpen={isModalVisible}
        className={"overflow-visible"}
        onClose={() => setIsModalVisible(false)}
      >
        <div className={"flex flex-col gap-2"}>
          <div className={"text-gray-900"}>
            <Trans
              i18nKey={"SalesAssistant_Deals_PlanActivity_Text"}
              values={{title: deal.title}}
              components={commonTransComponents}
            />
          </div>
          <div className={"mt-7 mb-3 h-px self-stretch bg-indigo-100"}></div>
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handleSubmit)}>
              <div className={"flex items-center justify-between"}>
                <div>
                  <div className={"font-bold text-gray-900"}>{t("SalesAssistant_Deals_PlanActivity_Activity")}</div>
                  <div className={"text-zinc-500"}>{t("SalesAssistant_Deals_PlanActivity_ActivityText")}</div>
                </div>
                <DropdownField
                  name={"task_type"}
                  label={""}
                  variant={"small"}
                  options={taskTypeOptions}
                  listWidth={"list-auto"}
                />
              </div>
              <div className={"my-3 h-px self-stretch bg-indigo-100"}></div>
              <div className={"mb-4 flex items-center justify-between"}>
                <div>
                  <div className={"font-bold text-gray-900"}>{t("SalesAssistant_Deals_PlanActivity_Date")}</div>
                  <div className={"text-zinc-500"}>{t("SalesAssistant_Deals_PlanActivity_DateText")}</div>
                </div>
                <div>
                  <DateField name={"task_date"} label={""} />
                </div>
              </div>
              <div className={"flex justify-end gap-2"}>
                <Button onClick={() => setIsModalVisible(false)} variant={"outlined"} color={"gray"}>
                  {t("SalesAssistant_Deals_PlanActivity_Cancel")}
                </Button>
                <ButtonForm>{t("SalesAssistant_Deals_PlanActivity_Confirm")}</ButtonForm>
              </div>
            </form>
          </FormProvider>
        </div>
      </VariantModal>
    </>
  )
}
